"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.selectedItemTheme = exports.Select = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _designTokensReact = require("@smartsheet/design-tokens-react");
var _lodestarIcons = require("@smartsheet/lodestar-icons");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _loggingStateService = require("@smartsheet/logging-state-service");
var _sdsThemeInternal = require("@smartsheet/sds-theme-internal");
var _ursaIcons = require("@smartsheet/ursa-icons");
var _fuzzysort = _interopRequireDefault(require("fuzzysort"));
var _react = require("react");
var _i18n = require("../../i18n/i18n");
var _useTheme = require("../../theme/utils/useTheme");
var _logging = require("../../util/logging");
var _typography = require("../typography");
var _SelectBasic = require("./components/select/SelectBasic");
var _SelectComboBox = require("./components/select/SelectComboBox");
var _selectV2Theme = require("./components/selectV2Theme");
var _FuzzySearchResult = require("./components/shared/FuzzySearchResult");
var _OptionBase = require("./components/shared/OptionBase");
var _constants = require("./constants");
var _SelectCatalog = require("./i18n/SelectCatalog");
var _utils = require("./utils");
/** @jsxRuntime classic */
/** @jsx jsx */

const selectedItemTheme = (0, _sdsThemeInternal.themed)({
  icon: {
    "lodestar-light": (0, _core.jsx)(_lodestarIcons.CheckThinIcon, {
      size: "small",
      color: (0, _designTokensReact.color)("color--icons--primary--default")
    }),
    "ursa-light": (0, _core.jsx)(_ursaIcons.CheckmarkIcon, {
      size: 20,
      color: (0, _designTokensReact.color)("color--icons--neutral--stronger")
    })
  }
});
exports.selectedItemTheme = selectedItemTheme;
const Select = _ref => {
  let {
    onChange,
    clientId,
    formatOption,
    innerRef,
    isOptionDisabled,
    isLoading = false,
    isSearchable = false,
    isReadonly = false,
    isClearable = false,
    useFuzzySearch = false,
    locale,
    maxMenuHeight = 200,
    maxOptionsUntilVirtualization = 50,
    menuId: providedMenuId,
    onCreateOption,
    optionHeight,
    optionToString = _utils.internalOptionToString,
    filterOption: providedFilterOption,
    inputValue,
    setInputValue,
    hideDropdownArrow = false,
    ...rest
  } = _ref;
  (0, _SelectCatalog.injectCatalog)();
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  if (process.env.NODE_ENV !== "production") {
    const hiddenInputNameDefined = typeof rest.hiddenInputName === "string";
    const getHiddenInputValueDefined = typeof rest.getHiddenInputValue === "function";
    if (hiddenInputNameDefined !== getHiddenInputValueDefined) {
      console.warn("MultiSelect: To use the hidden input, you must include both" + " props: getHiddenInputValue and hiddenInputName");
    }
  }
  const isCreatable = typeof onCreateOption === "function";
  const isComboBox = isSearchable || isCreatable;
  const defaultFilterOption = (0, _react.useCallback)((opt, input, fuzzySearchScoreMap) => {
    if (useFuzzySearch) {
      const result = _fuzzysort.default.single(input, optionToString(opt));
      if (result) {
        if (fuzzySearchScoreMap) {
          fuzzySearchScoreMap.set(opt, result.score);
        }
        return result.score >= _constants.FUZZYSORT_MIN_THRESHOLD;
      }
      return false;
    } else {
      return optionToString(opt)?.toLowerCase().startsWith(input);
    }
  }, [optionToString, useFuzzySearch]);
  const onChangeWithLogging = (0, _logging.useLoggingEnabledByDefault)((0, _logging.useLoggingDecorator)({
    controlType: "select",
    clientId
  }), {
    actionId: _loggingStateService.LogActionId.CLICK
  }, onChange);
  const defaultFormatOption = (option, index, selectedIndex, optionSpreadProps, isOptionSelected, metadata) => {
    let optionLabel = optionToString(option);
    if (useFuzzySearch) {
      optionLabel = (0, _core.jsx)(_FuzzySearchResult.FuzzySearchResult, {
        inputValue: metadata.inputValue,
        option: option,
        optionToString: optionToString
      });
    }
    return (0, _core.jsx)(_OptionBase.OptionBase, (0, _extends2.default)({
      height: optionHeight,
      "aria-disabled": metadata.isDisabled
    }, optionSpreadProps), (0, _core.jsx)(_typography.TypeRampV2, {
      lineClamp: 1,
      variant: _selectV2Theme.optionFontTheme.variant(themeMode),
      style: {
        color: "inherit"
      }
    }, optionLabel), isOptionSelected && (0, _core.jsx)("span", {
      style: {
        marginLeft: "auto",
        paddingLeft: 8
      }
    }, selectedItemTheme.icon(themeMode)));
  };
  const menuId = (0, _react.useMemo)(() => providedMenuId || (0, _lodestarUtils.genId)("select-menu"), [providedMenuId]);
  const sharedProps = {
    onChange: onChangeWithLogging,
    clientId,
    formatOption: formatOption || defaultFormatOption,
    isOptionDisabled: isOptionDisabled,
    isLoading,
    isReadonly,
    isClearable,
    locale,
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    menuId,
    optionToString,
    optionHeight,
    filterOption: providedFilterOption || defaultFilterOption,
    hideDropdownArrow
  };
  return (0, _core.jsx)(_i18n.I18nContextProvider, {
    locale: locale
  }, isComboBox ? (0, _core.jsx)(_SelectComboBox.SelectComboBox, (0, _extends2.default)({}, rest, sharedProps, {
    onCreateOption: onCreateOption,
    inputValue: inputValue,
    setInputValue: setInputValue,
    innerRef: innerRef
  })) : (0, _core.jsx)(_SelectBasic.SelectBasic, (0, _extends2.default)({}, rest, sharedProps, {
    innerRef: innerRef
  })));
};
exports.Select = Select;
Select.displayName = "Select";
//# sourceMappingURL=Select.js.map